import {
    CalendarDate,
    ClientId,
    CommunityId,
    genEntityId,
    getCurrentTimezone,
    IsoTimestamp,
    TourId,
    UserId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { TourStatus } from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useEffect, useReducer } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { TourData } from "../community-search/types";

interface TourEditorProps {
    tour: TourData | null;
    clientId: ClientId;
    communityId: CommunityId;
    onSaved: () => void;
    onClose: () => void;
}

interface FieldUpdate {
    field: keyof TourData;
    value: string | TourStatus | null;
}

interface ComponentState {
    status: "loading" | "saving" | "loaded" | "failed";
    tour: TourData | null;
    tourExists: boolean;
    pendingUpdates: FieldUpdate[];
    hasSaved: boolean;
    closeRequested: boolean;
    errorMessage: string;
}

type Action =
    | { type: "setTour"; tour: TourData }
    | { type: "enqueueUpdate"; fieldUpdates: FieldUpdate[] }
    | { type: "startCreate" }
    | { type: "startUpdate" }
    | { type: "requestClose" }
    | { type: "setError"; message: string }
    | { type: "updateField"; field: keyof TourData; value: string | TourStatus | null };

const defaultErrorMessage = "The tour failed to save.";

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "setTour": {
                draft.tour = a.tour;
                draft.status = "loaded";
                draft.tourExists = true;
                break;
            }
            case "enqueueUpdate": {
                draft.pendingUpdates.push(...a.fieldUpdates);
                break;
            }
            case "startCreate": {
                draft.status = "saving";
                draft.hasSaved = true;
                break;
            }
            case "startUpdate": {
                draft.status = "saving";
                draft.pendingUpdates = [];
                draft.hasSaved = true;
                break;
            }
            case "requestClose": {
                draft.closeRequested = true;
                break;
            }
            case "setError": {
                draft.status = "failed";
                draft.errorMessage = a.message || defaultErrorMessage;
                break;
            }
            case "updateField": {
                if (draft.tour) {
                    (draft.tour as Record<string, unknown>)[a.field] = a.value;
                }
                break;
            }
        }
    });
};

const emptyTour = (
    clientId: ClientId,
    communityId: CommunityId,
    userId: UserId,
): TourData => {
    const now = new Date();
    const todayDate = CalendarDate.fromLocalDate(now);
    return {
        id: genEntityId<TourId>(),
        clientId,
        communityId,
        userId,
        tourDate: todayDate.serialize(),
        tourTime: null,
        privateNotes: null,
        familyNotes: null,
        status: TourStatus.Scheduled,
        dateAdded: now.toISOString() as IsoTimestamp,
        updatedAt: now.toISOString() as IsoTimestamp,
    };
};

const initialState = (
    tour: TourData | null,
    clientId: ClientId,
    communityId: CommunityId,
    userId: UserId,
): ComponentState => {
    return {
        status: "loaded",
        tour: tour ?? emptyTour(clientId, communityId, userId),
        tourExists: !!tour,
        pendingUpdates: [],
        hasSaved: false,
        closeRequested: false,
        errorMessage: "",
    };
};

export const TourEditor: React.FC<TourEditorProps> = props => {
    const { tour, clientId, communityId, onSaved, onClose } = props;
    const currentUserId = useCurrentUserId();

    const [state, dispatch] = useReducer(
        reducer,
        initialState(tour, clientId, communityId, currentUserId),
    );

    const trpcClient = trpc.useTRPC();

    const createMutation = useMutation({
        ...trpcClient.tours.create.mutationOptions(),
        onSuccess: data => {
            dispatch({ type: "setTour", tour: mapTourToData(data) });
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const updateMutation = useMutation({
        ...trpcClient.tours.update.mutationOptions(),
        onSuccess: data => {
            dispatch({ type: "setTour", tour: mapTourToData(data) });
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const deleteMutation = useMutation({
        ...trpcClient.tours.delete.mutationOptions(),
        onSuccess: () => {
            onSaved();
            onClose();
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const doUpdate = useCallback(
        (tourId: TourId, updatesToMake: FieldUpdate[]) => {
            dispatch({ type: "startUpdate" });

            const params = updatesToMake.reduce(
                (agg, update) => {
                    if (update.field === "tourDate") {
                        return {
                            ...agg,
                            tourDate: update.value as string,
                            tourTimeZone: getCurrentTimezone(),
                        };
                    }
                    if (update.field === "tourTime") {
                        return {
                            ...agg,
                            tourTime: update.value as string | null,
                            tourTimeZone: getCurrentTimezone(),
                        };
                    }
                    if (update.field === "privateNotes") {
                        return { ...agg, privateNotes: update.value as string };
                    }
                    if (update.field === "familyNotes") {
                        return { ...agg, familyNotes: update.value as string };
                    }
                    if (update.field === "status") {
                        return { ...agg, status: update.value as TourStatus };
                    }
                    return agg;
                },
                { id: tourId },
            );

            updateMutation.mutate(params);
        },
        [updateMutation],
    );

    const handleFieldChange = useCallback(
        (field: keyof TourData, value: string | TourStatus | null) => {
            dispatch({ type: "updateField", field, value });

            const fieldUpdate: FieldUpdate = { field, value };

            if (state.status === "saving") {
                dispatch({ type: "enqueueUpdate", fieldUpdates: [fieldUpdate] });
            } else if (state.status === "loaded") {
                if (state.tourExists && state.tour) {
                    const updatesToMake = [...state.pendingUpdates, fieldUpdate];
                    doUpdate(state.tour.id, updatesToMake);
                } else {
                    dispatch({ type: "startCreate" });
                    dispatch({ type: "enqueueUpdate", fieldUpdates: [fieldUpdate] });
                    createMutation.mutate({
                        communityId,
                        clientId,
                        userId: currentUserId,
                        tourDate:
                            field === "tourDate"
                                ? (value as string)
                                : (state.tour?.tourDate as unknown as string) || "",
                        tourTime:
                            field === "tourTime"
                                ? (value as string | null)
                                : state.tour?.tourTime,
                        privateNotes:
                            field === "privateNotes"
                                ? (value as string)
                                : state.tour?.privateNotes,
                        familyNotes:
                            field === "familyNotes"
                                ? (value as string)
                                : state.tour?.familyNotes,
                        tourTimeZone: getCurrentTimezone(),
                        status:
                            field === "status"
                                ? (value as TourStatus)
                                : state.tour?.status || TourStatus.Scheduled,
                    });
                }
            }
        },
        [
            state.status,
            state.tourExists,
            state.tour,
            state.pendingUpdates,
            communityId,
            clientId,
            currentUserId,
            createMutation,
            doUpdate,
        ],
    );

    const handleDelete = useCallback(() => {
        if (state.tour && state.tourExists) {
            deleteMutation.mutate({ id: state.tour.id });
        }
    }, [state.tour, state.tourExists, deleteMutation]);

    const handleClose = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            dispatch({ type: "requestClose" });
        },
        [dispatch],
    );

    useEffect(() => {
        if (state.status === "loaded" && state.tour && state.hasSaved) {
            if (state.pendingUpdates.length > 0) {
                doUpdate(state.tour.id, state.pendingUpdates);
            } else {
                onSaved();
            }
        }
    }, [state.tour]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            state.closeRequested &&
            state.status === "loaded" &&
            state.pendingUpdates.length === 0
        ) {
            onClose();
        }
        if (state.closeRequested && state.status === "failed") {
            onClose();
        }
    }, [state.closeRequested, state.status, state.pendingUpdates.length, onClose]);

    return (
        <Panel
            type={PanelType.medium}
            headerText={state.tourExists ? "Edit Tour" : "Add Tour"}
            onDismiss={onClose}
            isOpen={true}>
            <div className="space-y-4">
                <div>
                    <label className="block mb-1">Tour Date</label>
                    <input
                        type="date"
                        value={(state.tour?.tourDate as unknown as string) || ""}
                        onChange={e => handleFieldChange("tourDate", e.target.value)}
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <label className="block mb-1">Tour Time (optional)</label>
                    <input
                        type="time"
                        value={state.tour?.tourTime || ""}
                        onChange={e =>
                            handleFieldChange("tourTime", e.target.value || null)
                        }
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <Select
                    label="Status"
                    value={state.tour?.status || TourStatus.Scheduled}
                    onChange={e =>
                        handleFieldChange("status", e.target.value as TourStatus)
                    }>
                    <option value={TourStatus.Scheduled}>Scheduled</option>
                    <option value={TourStatus.Completed}>Completed</option>
                    <option value={TourStatus.Cancelled}>Cancelled</option>
                </Select>

                <AutosavingInput
                    label="Tour Notes (shared with family)"
                    multiLine={true}
                    initial={state.tour?.familyNotes || ""}
                    onCommit={value => handleFieldChange("familyNotes", value)}
                />

                <AutosavingInput
                    label="Internal Notes"
                    multiLine={true}
                    initial={state.tour?.privateNotes || ""}
                    onCommit={value => handleFieldChange("privateNotes", value)}
                />

                {state.errorMessage ? (
                    <div className="text-red-600 text-sm">{state.errorMessage}</div>
                ) : null}

                <div className="flex justify-between items-center pt-4">
                    {state.tourExists ? (
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={handleDelete}
                            confirm={{
                                title: "Delete Tour",
                                message: "Are you sure you want to delete this tour?",
                            }}
                        />
                    ) : (
                        <div />
                    )}
                    <div className="flex items-center space-x-2">
                        {state.closeRequested ? <Spinner /> : null}
                        <PrimaryButton
                            onClick={handleClose}
                            disabled={state.closeRequested}>
                            Close
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Panel>
    );
};

interface TrpcTourDto {
    id: TourId;
    communityId: CommunityId;
    clientId?: ClientId | null;
    userId: UserId;
    tourDate: string;
    tourTime?: string | null;
    privateNotes?: string | null;
    familyNotes?: string | null;
    tourTimeZone?: string | null;
    status: TourStatus;
    dateAdded: string;
    updatedAt: string;
}

function mapTourToData(tour: TrpcTourDto): TourData {
    const tourDateSerialized = CalendarDate.parse(tour.tourDate)
        .map(cd => cd.serialize())
        .getOrElse(CalendarDate.today.serialize());
    return {
        id: tour.id,
        communityId: tour.communityId,
        clientId: tour.clientId,
        userId: tour.userId,
        tourDate: tourDateSerialized,
        tourTime: tour.tourTime,
        privateNotes: tour.privateNotes,
        familyNotes: tour.familyNotes,
        status: tour.status,
        dateAdded: tour.dateAdded as IsoTimestamp,
        updatedAt: tour.updatedAt as IsoTimestamp,
    };
}
